
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import Modal from '@/components/shared/Modal.vue';
import WarningModal from '@/components/shared/WarningModal.vue';

const namespace: string = 'cart';

@Component<AvailableDiscountsModal>({
	components: {
		Modal,
		WarningModal
	}
})
export default class AvailableDiscountsModal extends Vue {
	@Action('selectMembershipDiscounts', { namespace }) private selectMembershipDiscounts!: (discounts: Set<string>) => void;
	@Action('clearCartItems', { namespace }) private clearCartItems!: () => void;
	@Action('checkIfMemberAndApplyPromo', { namespace: 'auth' }) private checkIfMemberAndApplyPromo!: (payload: { identifier: string, discount?: PunchhPromo | MemberPromo }) => void;
	@Getter('getMemberInfo', { namespace }) private memberInfo!: CheckoutMemberInfo | null;
	@Prop({ type: Array, required: true }) private discounts!: (PunchhPromo | MemberPromo)[];

	private selectedDiscounts: Set<string> = new Set();
	private discountFromDifferentProvider: PunchhPromo | MemberPromo | null = null;

	/**
	 * Initialize selected discounts from currently applied promos
	 *
	 * @return {void}
	 */
	private created(): void {
		this.selectedDiscounts = new Set(
			this.discounts
				.filter((discount: PunchhPromo | MemberPromo) => this.memberInfo?.promosApplied.has(discount.id))
				.map((discount: PunchhPromo | MemberPromo) => discount.id)
		);
	}

	/**
	 * Check if a discount is currently selected
	 *
	 * @param {PunchhPromo | MemberPromo} discount
	 * @return {boolean}
	 */
	private isSelected(discount: PunchhPromo | MemberPromo): boolean {
		return this.selectedDiscounts.has(discount.id);
	}

	/**
	 * Toggle a discount's selection state
	 * If the discount provider is different from the currently selected one, open the warning modal
	 *
	 * @param {PunchhPromo | MemberPromo} discount
	 * @return {void}
	 */
	private toggleDiscount(discount: PunchhPromo | MemberPromo): void {
		if (this.memberInfo && this.memberInfo.provider !== discount.provider) {
			this.discountFromDifferentProvider = discount;
			return;
		}
		this.selectedDiscounts = this.selectedDiscounts.has(discount.id) ? new Set() : new Set([discount.id]);
	}

	/**
	 * Update selected discounts in the store and close the modal
	 *
	 * @return {void}
	 */
	private submitDiscounts(): void {
		const haveDiscountsChanged = this.checkIfSelectionsChanged();
		this.selectMembershipDiscounts(this.selectedDiscounts);
		this.$emit('close', haveDiscountsChanged);
	}

	/**
	 * Compare current selections against previously applied promos
	 * Used to determine if set costs should be run again when changing selected discounts in the cart
	 *
	 * @return {boolean}
	 */
	private checkIfSelectionsChanged(): boolean {
		const currentlyApplied = this.memberInfo?.promosApplied || new Set<string>();

		if (this.selectedDiscounts.size !== currentlyApplied.size) {
			return true;
		}

		for (const id of this.selectedDiscounts) {
			if (!currentlyApplied.has(id)) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Switch the discount then apply discount and clear the cart
	 * The must be cleared after the emitting membership-applied because of the discounts banner in the cart
	 * When the cart is cleared, the discounts banner will be destroyed and the event to refresh the menus will never emit
	 *
	 * @return {Promise<void>}
	 */
	private async switchProvider(): Promise<void> {
		if (this.discountFromDifferentProvider) {
			this.selectedDiscounts = new Set([this.discountFromDifferentProvider.id]);
			await this.checkIfMemberAndApplyPromo({
				identifier: this.memberInfo?.identifier || '',
				discount: this.discountFromDifferentProvider
			});
			this.$emit('membership-applied');
			this.clearCartItems();
			this.discountFromDifferentProvider = null;
		}
	}
}
